import CryptoJS from 'crypto-js';
import Base64 from './_base64';
import nowInUTC from './nowInUTC';

interface Params extends RequestParams {
  apiKey?: string,
  apiSecret?: string,
  countryCode?: string,
  id?: string
  params?: any
}

interface RequestParams {
  data?: any,
  headers?: any,
  method: 'GET' | 'PATCH' | 'POST',
  params?: any,
  url: string
}

/**
 * Carried over from the ole Parent site repo... -_- This logic takes care of
 * generating custom request headers, jwts etc.... I have just refactored it to
 * read in a similar fashion with the rest of this repo - functional programming.
 *
 * Documentation on logic:
 * https://azoomee.atlassian.net/wiki/spaces/API/pages/1219198977/JWT+Signed+Requests
 */

/**
 * Sort object
 */
const getSortedKeyArray = (object: any) => {
  var keys = [],
    key;
  for (key in object) {
    if (object.hasOwnProperty(key)) {
      keys.push(key);
    }
  }
  keys.sort();
  return keys;
}

/**
 * Get current path
 */
const getPath = (url: string) => {
  var parser = document.createElement('a');
  parser.href = url;
  if (parser.pathname.charAt(0) !== '/') {
    return '/' + parser.pathname;
  }

  return parser.pathname;
}

/**
 * Get query params
 */
const getQueryParamsString = (queryParams: any) => {
  let keys = getSortedKeyArray(queryParams),
    i,
    queryString = '',
    first = true;

  for (i = 0; i < keys.length; i++) {
    if (first === false) {
      queryString += '&';
    } else {
      first = false;
    }
    queryString +=
      encodeURIComponent(keys[i].toLowerCase()) +
      '=' +
      encodeURIComponent(queryParams[keys[i]].toString().toLowerCase());
  }
  return queryString;
};

/**
 * Get host string
 */
const getHostString = (url: string) => {
  var parser = document.createElement('a');
  parser.href = url;
  return parser.host.split(':')[0];
};

/**
 * Get header string
 */
const getHeadersString = (config: any) => {
  const { headers, url } = config;
  const contentType = (headers && headers['Content-Type']) || '';
  const dateTime = (headers && headers['X-AZ-REQ-DATETIME']) || '';

  var headerString = '';
  if (contentType && config.data) {
    headerString =
      'content-type=' +
      encodeURIComponent(contentType.toLowerCase()) +
      '&';
  }
  headerString +=
    'host=' +
    encodeURIComponent(getHostString(url).toLowerCase()) +
    '&' +
    encodeURIComponent('X-AZ-REQ-DATETIME'.toLowerCase()) +
    '=' +
    encodeURIComponent(dateTime.toLowerCase());
  return headerString;
}

/**
 * Build body string
 */
const buildBodyString = (body: any) => {
  if (typeof body === 'object') {
    body = JSON.stringify(body);
  }
  if (!body) {
    return '';
  }
  return Base64.encode(body);
}

/**
 * Build token
 */
const buildToken = (headerClaimsString: string, secret: string) => {
  var hash = CryptoJS.HmacSHA256(headerClaimsString, secret);
  return CryptoJS.enc.Base64.stringify(hash);
};

/**
 * Build signature
 */
const buildSignature = (config: RequestParams, secret: string) => {
  const { data, method, params, url } = config;
  let signatureString = '';
  signatureString += method + '\n';
  signatureString += encodeURIComponent(getPath(url)) + '\n';
  signatureString += getQueryParamsString(params) + '\n';
  signatureString += getHeadersString(config) + '\n';
  if (data) {
    signatureString += buildBodyString(data);
  } else {
    signatureString += '';
  }

  return buildToken(signatureString, secret);
};

/**
 * Build token body
 */
const buildBody = (config: RequestParams, userId: string, secret: string, key: string) => {
  // const epochStamp = parseInt(Date.now() / 1000 , 10);
  var body = {
    iss: userId,
    aud: document.location.host,
    applicationClaim: {
      signature: buildSignature(config, secret),
      parentKey: 'undefined',
    },
  };

  return Base64.encode(JSON.stringify(body));
}

/**
 * Generate a jwt
 */
export const generateToken = (config: RequestParams, userId: string, secret: string, key: any) => {
  let jwtToken;
  jwtToken = Base64.encode(JSON.stringify({ alg: "HS256", kid: key }));
  jwtToken += '.' + buildBody(config, userId, secret, key);
  jwtToken += '.' + buildToken(jwtToken, secret);

  return jwtToken;
};

export const generateSignedRequestConfig: (args: Params) => any = ({
  apiKey,
  apiSecret,
  data,
  headers = {},
  id,
  method,
  params,
  url }: Params) => {
  if (!apiSecret || !apiKey || !id) {
    return null;
  }
  headers['X-AZ-REQ-DATETIME'] = nowInUTC();
  data && (headers['Content-Type'] = 'application/json;charset=UTF-8')
  const jwtToken = generateToken({ data, headers, method, params, url }, id, apiSecret, apiKey);

  headers['X-AZ-AUTH-TOKEN'] = jwtToken;
  headers["X-AZ-APPVERSION"] = "website-billing";

  return { data, headers, method, params, url };
}